import { fetchPaged, type RangeableThenableQuery } from "@/data/paging";
import { supabase } from "@/lib/supabase";
import type {
  ActivityChartRow,
  ChartQueryOpts,
  ChartRow,
  SportKey,
} from "@/types/chart.types";
import { resolveChartTimeWindow, rowsToChartData } from "@/utils/chart.utils";

const HARD_LIMIT = 10_000;

async function fetchChartActivities(
  opts: ChartQueryOpts,
): Promise<ActivityChartRow[]> {
  const { userId, metric, grain, search, sports, to, from } = opts;

  const { fromIso, toExclusiveIso } = resolveChartTimeWindow({
    grain,
    to,
    from,
  });

  return fetchPaged<ActivityChartRow>(
    () => {
      const selectCols =
        metric === "count"
          ? "start_date,sport_type,type"
          : `start_date,sport_type,type,${metric}`;

      let req = supabase
        .from("activities")
        .select(selectCols)
        .eq("user_id", userId)
        .eq("is_deleted", false)
        .order("start_date", { ascending: false });

      // Search
      if (search?.trim()) {
        const term = search.trim().replace(/[%_]/g, "\\$&");
        req = req.or(
          `name.ilike.%${term}%,sport_type.ilike.%${term}%,type.ilike.%${term}%`,
        );
      }

      // Multi-sport
      switch (sports?.mode) {
        case "include": {
          if (sports.sports.length === 0) {
            req = req.eq("sport_type", "__none__");
          } else {
            req = req.in("sport_type", sports.sports);
          }
          break;
        }
        case "exclude": {
          if (sports.sports.length > 0) {
            req = req.not("sport_type", "in", `(${sports.sports.join(",")})`);
          }
          break;
        }
        case "all":
        default:
          break;
      }

      // Date bounds
      if (fromIso) req = req.gte("start_date", fromIso);
      if (toExclusiveIso) req = req.lt("start_date", toExclusiveIso);

      return req as RangeableThenableQuery<ActivityChartRow>;
    },
    {
      limit: opts.limit,
      hardLimit: HARD_LIMIT,
    },
  );
}

export async function fetchChartData(opts: ChartQueryOpts): Promise<{
  chartData: ChartRow[];
  series: SportKey[];
  totalValue?: number;
}> {
  const rows = await fetchChartActivities(opts);
  return rowsToChartData(rows, opts);
}
